export type AudioStatus = 'idle' | 'ready' | 'blocked' | 'paused' | 'unavailable';
export class Soundscape {
  private context?: AudioContext;
  private ambient?: { oscillators: OscillatorNode[]; gain: GainNode };
  private tones = new Set<OscillatorNode>();
  private positiveUntil = 0;
  enabled = true;
  private paused = false;
  private unavailable = false;
  private effects?: GainNode;
  private effectsVolume = 1;
  private musicVolume = 1;
  private lastSelect = -Infinity;
  private attempted = false;
  constructor(private changed: () => void = () => {}) {}
  get state(): AudioStatus {
    if (this.paused) return 'paused';
    if (this.unavailable) return 'unavailable';
    if (!this.context || (!this.attempted && this.context.state !== 'running')) return 'idle';
    return this.context.state === 'running' ? 'ready' : 'blocked';
  }
  volumes(effects: number, music: number): void {
    const clamp = (value: number) => Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 1;
    this.effectsVolume = clamp(effects); this.musicVolume = clamp(music);
    if (this.context) {
      this.effects?.gain.setValueAtTime(this.effectsVolume, this.context.currentTime);
      if (this.ambient) {
        this.ambient.gain.gain.cancelScheduledValues(this.context.currentTime);
        this.ambient.gain.gain.setTargetAtTime(.025 * this.musicVolume, this.context.currentTime, .03);
      }
    }
    if (!this.enabled || !this.effectsVolume) this.cancelRewards();
  }
  /** Prepare an inert context before play; device initialization can block a first tap. */
  prepare(): void {
    try {
      if (!this.context || this.context.state === 'closed') {
        this.cancelRewards(); this.stopAmbient();
        this.context = new AudioContext();
        this.effects = this.context.createGain(); this.effects.gain.value = this.effectsVolume;
        this.effects.connect(this.context.destination);
        const context = this.context;
        context.addEventListener('statechange', () => {
          if (context !== this.context) return;
          if (this.paused && context.state === 'running') void context.suspend().catch(() => {});
          this.changed();
        });
      }
      this.unavailable = false;
    } catch { this.unavailable = true; }
    this.changed();
  }
  unlock(): void {
    this.paused = false; this.prepare(); this.attempted = true;
    if (this.context && this.context.state !== 'running') void this.context.resume().then(() => this.changed()).catch(() => this.changed());
    this.changed();
  }
  reward(kind: 'select' | 'success' | 'special' | 'complete' | 'exceed' | 'invalid'): void {
    if (!this.enabled || !this.effectsVolume || this.paused || document.hidden) return;
    this.unlock();
    const context = this.context;
    // Never queue stale rewards behind a blocked audio context.
    if (!context || context.state !== 'running' || (kind === 'select' && (context.currentTime < this.positiveUntil || context.currentTime - this.lastSelect < .045))) return;
    if (kind === 'select') this.lastSelect = context.currentTime;
    const notes = kind === 'exceed' ? [659, 831, 1047, 1319] : kind === 'complete' ? [523, 659, 784, 1047] : kind === 'special' ? [523, 784, 1047] : kind === 'success' ? [523, 659] : kind === 'select' ? [440] : [220];
    if (this.tones.size + notes.length > 12) this.cancelRewards();
    if (kind !== 'select' && kind !== 'invalid') this.positiveUntil = context.currentTime + notes.length * .08 + .22;
    notes.forEach((frequency, i) => {
      const oscillator = context.createOscillator(), gain = context.createGain();
      oscillator.type = 'sine'; oscillator.frequency.value = frequency;
      const start = context.currentTime + i * 0.08;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(kind === 'select' ? 0.015 : 0.07, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + (kind === 'invalid' ? 0.12 : 0.22));
      oscillator.connect(gain).connect(this.effects!);
      this.tones.add(oscillator);
      oscillator.start(start); oscillator.stop(start + (kind === 'invalid' ? 0.14 : 0.25));
      oscillator.onended = () => { this.tones.delete(oscillator); oscillator.disconnect(); gain.disconnect(); };
    });
  }
  private stopAmbient(): void {
    const ambient = this.ambient; this.ambient = undefined;
    ambient?.oscillators.forEach(oscillator => { try { oscillator.stop(); } catch { /* Already stopped. */ } });
  }
  resume(music: boolean): void { this.paused = false; this.music(music); this.changed(); }
  music(enabled: boolean): void {
    if (!enabled || document.hidden) { this.stopAmbient(); this.changed(); return; }
    this.unlock();
    if (!this.context || this.ambient) return;
    const context = this.context, gain = context.createGain();
    // Midrange notes remain audible on phone speakers; a lone bass sine may disappear.
    gain.gain.setValueAtTime(0, context.currentTime);
    gain.gain.linearRampToValueAtTime(0.025 * this.musicVolume, context.currentTime + 0.8);
    gain.connect(context.destination);
    const oscillators = [261.63, 329.63, 392].map(frequency => {
      const oscillator = context.createOscillator();
      oscillator.frequency.value = frequency; oscillator.type = 'sine';
      oscillator.connect(gain); oscillator.start();
      oscillator.onended = () => oscillator.disconnect();
      return oscillator;
    });
    let remaining = oscillators.length;
    for (const oscillator of oscillators) oscillator.addEventListener('ended', () => { if (--remaining === 0) gain.disconnect(); });
    this.ambient = { oscillators, gain };
  }
  cancelRewards(): void { for (const tone of this.tones) { try { tone.stop(); } catch { /* Already ended. */ } } this.tones.clear(); this.positiveUntil = 0; this.lastSelect = -Infinity; }
  suspend(): void { this.paused = true; this.cancelRewards(); this.stopAmbient(); void this.context?.suspend().catch(() => {}); this.changed(); }
}
